import type { CSSProperties } from 'react';
import { z } from 'zod';

export const BadgeOfferSchema = z.object({
  type: z.enum(['system', 'promo']).optional(),
  label: z.string(),
  styles: z.object({
    stroke: z.object({
      width: z.string(),
      gradient: z.string(),
    }).optional(),
    cssStyles: z.custom<CSSProperties>(),
  }).optional(),
});

const BadgeOfferValueSchema = z
  .union([BadgeOfferSchema, z.array(BadgeOfferSchema)])
  .transform((value) => {
    const badges = Array.isArray(value) ? value : [value];

    return badges.map(badge => ({ ...badge, type: badge.type ?? 'promo' }));
  });

export const RelatedItemSchema = z
  .object({
    id: z.string(),
    name: z.string().optional(),
  })
  .passthrough();

export const OfferPresentationSchema = z
  .object({
    isVip: z.boolean().optional(),
    isPromo: z.boolean().optional(),
    dailyBonusUpTo: z.number().optional(),
    badge: BadgeOfferValueSchema.nullish(),
  })
  .passthrough();

export const ProductPresentationSchema = z
  .object({
    order: z.number().optional(),
    beforeSalePrice: z.number().optional(),
    isDefaultPromo: z.boolean().optional(),
    relatedProducts: z.array(RelatedItemSchema).optional(),
  })
  .passthrough();

export const ProductAssetDtoSchema = z
  .object({
    id: z.string(),
    key: z.string().nullable().optional(),
    path: z.string(),
    size: z.number().optional(),
    mimeType: z.string().optional(),
    meta: z.any().optional(),
    appearance: z.string().nullable().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough();

export const OfferDtoSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    productId: z.string(),
    bsc: z.number(),
    gc: z.number(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    assets: z.array(ProductAssetDtoSchema).default([]),
    presentation: OfferPresentationSchema.nullish(),
    createdAt: z.string().optional(),
  })
  .passthrough();

export const ProductDtoSchema = z
  .object({
    id: z.string(),
    alias: z.string().nullable().optional(),
    name: z.string(),
    price: z.number(),
    sc: z.number(),
    presentation: ProductPresentationSchema.nullable().optional(),
    assets: z.array(ProductAssetDtoSchema).default([]).optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
  })
  .passthrough();

export const BundleOfferDtoSchema = z
  .object({
    id: z.string(),
    userId: z.string().optional(),
    offerId: z.string().optional(),
    productId: z.string(),
    offer: OfferDtoSchema,
    product: ProductDtoSchema,
    count: z.number().optional(),
    createdAt: z.string().optional(),
  })
  .passthrough();

export const BundleOffersResponseSchema = z.array(BundleOfferDtoSchema);

export type BadgeOffer = z.infer<typeof BadgeOfferSchema>;
export type NormalizedBadgeOffer = BadgeOffer & { type: NonNullable<BadgeOffer['type']> };
export type RelatedItem = z.infer<typeof RelatedItemSchema>;
export type OfferPresentation = z.infer<typeof OfferPresentationSchema>;
export type OfferPresentationInput = z.input<typeof OfferPresentationSchema>;
export type ProductPresentation = z.infer<typeof ProductPresentationSchema>;
export type ProductAssetDto = z.infer<typeof ProductAssetDtoSchema>;
export type OfferDto = z.infer<typeof OfferDtoSchema>;
export type ProductDto = z.infer<typeof ProductDtoSchema>;
export type BundleOfferDto = z.infer<typeof BundleOfferDtoSchema>;

export const normalizeBadges = (badges?: BadgeOffer | BadgeOffer[] | null): NormalizedBadgeOffer[] => {
  if (!badges) {
    return [];
  }

  const badgeList = Array.isArray(badges) ? badges : [badges];

  return badgeList.map(badge => ({ ...badge, type: badge.type ?? 'promo' }));
};
